// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { KeycloakProvider } from "./context/KeycloakProvider";
import { ThemeProvider } from "./context/ThemeContext";
import AppRoutes from "./routes/AppRoutes";
import "./App.css";
import { NotificationProvider } from "./context/NotificationContext";
import { useKeycloak } from "./context/KeycloakProvider";
import { usePermissions } from "./hooks/usePermissions";
import UserSyncHandler from "./components/auth/UserSyncHandler";
import PermissionPopup from "./components/auth/NoAccessPage";
import TenantPrimaryColor from "./components/TenantPrimaryColor";
import TenantLocalization from "./components/TenantLocalization";
import LoadingScreen from "./components/LoadingScreen";
import { useTheme } from "./context/ThemeContext";

const AppContent = () => {
  const [showNoAccessPopup, setShowNoAccessPopup] = useState(false);
  const [hasShownPopup, setHasShownPopup] = useState(false);
  const { isDarkMode } = useTheme();

  const {
    authenticated,
    loading: keycloakLoading,
    syncedUser,
    syncLoading,
  } = useKeycloak();

  const {
    loading: permissionsLoading,
    userHasRoles,
    isInitialized,
  } = usePermissions();

  useEffect(() => {
    if (authenticated && !userHasRoles && isInitialized && !hasShownPopup) {
      setShowNoAccessPopup(true);
      setHasShownPopup(true);
    }
  }, [authenticated, userHasRoles, isInitialized, hasShownPopup]);

  const handleClosePopup = () => {
    setShowNoAccessPopup(false);
  };
  const tenantSettingsReady = authenticated && syncedUser;
  const isBootstrapping =
    keycloakLoading ||
    (authenticated && !syncedUser && syncLoading) ||
    (authenticated && !isInitialized) ||
    (authenticated && userHasRoles && permissionsLoading);

  if (isBootstrapping) {
    return (
      <>
        {tenantSettingsReady && <TenantPrimaryColor />}
        {tenantSettingsReady && <TenantLocalization />}
        <LoadingScreen isDarkMode={isDarkMode} />
      </>
    );
  }

  return (
    <>
      <TenantPrimaryColor />
      <TenantLocalization />
      <AppRoutes />
      <PermissionPopup
        isOpen={showNoAccessPopup}
        onClose={handleClosePopup}
        showLogout={false}
      />
    </>
  );
};

function App() {
  return (
    <KeycloakProvider>
      <ThemeProvider>
        <UserSyncHandler>
          <NotificationProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <AppContent />
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: "#363636",
                    color: "#fff",
                  },
                  success: {
                    duration: 3000,
                    iconTheme: {
                      primary: "#4ade80",
                      secondary: "#fff",
                    },
                  },
                  error: {
                    duration: 5000,
                    iconTheme: {
                      primary: "#ef4444",
                      secondary: "#fff",
                    },
                  },
                }}
              />
            </Router>
          </NotificationProvider>
        </UserSyncHandler>
      </ThemeProvider>
    </KeycloakProvider>
  );
}

export default App;
